<template>
  <HrPageLayout
    title="HR Meetings"
    subtitle="Schedule and manage HR meetings"
    icon="📅"
    :breadcrumb-items="[{ label: 'HR Management' }, { label: 'Meetings' }]"
  >
    <template #header-actions>
      <button @click="createMeeting" class="hr-btn hr-btn-primary">
        <i class="bx bx-plus"></i> Schedule Meeting
      </button>
      <button @click="refreshData" class="hr-btn hr-btn-secondary">
        <i class="bx bx-refresh"></i> Refresh
      </button>
    </template>

    <div class="hr-page-content" style="padding: 20px;">
      <!-- Statistics cards -->
      <div class="hr-metrics-grid hr-mb-4">
        <!-- Total Meetings -->
        <div class="hr-metric-card gradient-1 hr-premium-card">
          <div class="hr-metric-icon"><i class="bx bx-calendar-check"></i></div>
          <div class="hr-metric-content">
            <p class="hr-metric-label">Total Meetings</p>
            <h2 class="hr-metric-value">{{ metrics.totalMeetings || 0 }}</h2>
            <span class="hr-metric-subtext">Past 30 days</span>
          </div>
        </div>

        <!-- Upcoming Meetings -->
        <div class="hr-metric-card gradient-2 hr-premium-card">
          <div class="hr-metric-icon"><i class="bx bx-calendar-event"></i></div>
          <div class="hr-metric-content">
            <p class="hr-metric-label">Upcoming</p>
            <h2 class="hr-metric-value">{{ metrics.upcomingMeetings || 0 }}</h2>
            <span class="hr-metric-subtext">Next 7 days</span>
          </div>
        </div>

        <!-- This Month -->
        <div class="hr-metric-card gradient-3 hr-premium-card">
          <div class="hr-metric-icon"><i class="bx bx-calendar-star"></i></div>
          <div class="hr-metric-content">
            <p class="hr-metric-label">This Month</p>
            <h2 class="hr-metric-value">{{ metrics.meetingsThisMonth || 0 }}</h2>
            <span class="hr-metric-subtext">{{ currentMonthName }}</span>
          </div>
        </div>

        <!-- Today's Meetings -->
        <div class="hr-metric-card gradient-4 hr-premium-card">
          <div class="hr-metric-icon"><i class="bx bx-time-five"></i></div>
          <div class="hr-metric-content">
            <p class="hr-metric-label">Today</p>
            <h2 class="hr-metric-value">{{ metrics.todayMeetings || 0 }}</h2>
            <span class="hr-metric-subtext">{{ today }}</span>
          </div>
        </div>
      </div>

      <!-- Filters Section -->
      <div class="hr-chart-card hr-mb-4 hr-premium-card">
        <div class="hr-chart-header">
          <h5 class="hr-chart-title"><i class="bx bx-filter-alt"></i> Filter Meetings</h5>
        </div>
        <div class="hr-chart-body">
          <div class="hr-filters-grid">
            <div class="hr-filter-group">
              <label>Meeting Type</label>
              <select v-model="filters.meetingType" class="hr-select">
                <option value="">All Types</option>
                <option v-for="type in meetingTypes" :key="type" :value="type">{{ type }}</option>
              </select>
            </div>
            <div class="hr-filter-group">
              <label>Department</label>
              <select v-model="filters.departmentId" class="hr-select">
                <option value="">All Departments</option>
                <option v-for="dept in departments" :key="dept.id" :value="dept.id">{{ dept.departmentName }}</option>
              </select>
            </div>
            <div class="hr-filter-group" style="grid-column: span 2;">
              <label>Date Range</label>
              <div class="hr-date-range">
                <input v-model="filters.startDate" type="date" class="hr-input">
                <span class="range-sep">to</span>
                <input v-model="filters.endDate" type="date" class="hr-input">
              </div>
            </div>
            <div class="hr-filter-group hr-search-wrap">
              <label>Search</label>
              <input type="text" v-model="filters.search" class="hr-input" placeholder="Search title...">
              <i class="bx bx-search search-icon"></i>
            </div>
          </div>
        </div>
      </div>

      <!-- Content Grid: Charts & Upcoming -->
      <div class="hr-content-grid hr-mb-4">
        <!-- Meeting Type Breakdown -->
        <div class="hr-chart-card hr-premium-card" style="margin-bottom: 0;">
          <div class="hr-chart-header">
            <h5 class="hr-chart-title"><i class="bx bx-pie-chart-alt-2"></i> Meetings by Type</h5>
          </div>
          <div class="hr-chart-body">
            <div v-if="metrics.typeCounts && Object.keys(metrics.typeCounts).length > 0">
              <table class="hr-table-modern">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th class="hr-text-center">Count</th>
                    <th>Distribution</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(count, type) in metrics.typeCounts" :key="type">
                    <td><strong>{{ type }}</strong></td>
                    <td class="hr-text-center">{{ count }}</td>
                    <td>
                      <div class="hr-flex-center">
                        <div class="hr-progress-bar hr-flex-1">
                          <div class="hr-progress-fill" :class="getMeetingTypeColor(type)" :style="`width: ${getMeetingTypePercentage(type)}%`"></div>
                        </div>
                        <span class="hr-small hr-text-muted">{{ getMeetingTypePercentage(type) }}%</span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="hr-meet-empty">
              <i class="bx bx-chart"></i>
              <p>No meeting type data available</p>
            </div>
          </div>
        </div>

        <!-- Upcoming Meetings List -->
        <div class="hr-chart-card hr-premium-card" style="margin-bottom: 0;">
          <div class="hr-chart-header">
            <h5 class="hr-chart-title"><i class="bx bx-calendar-star"></i> Upcoming Meetings</h5>
          </div>
          <div class="hr-chart-body" style="padding: 12px;">
            <div v-if="upcomingMeetings.length > 0">
              <div v-for="meeting in upcomingMeetings" :key="meeting.id" class="hr-meet-item hr-flex-center hr-mb-2">
                <div class="hr-meet-date-badge hr-me-3">
                  <span class="date-month">{{ formatDateMonth(meeting.startDate) }}</span>
                  <span class="date-day">{{ formatDateDay(meeting.startDate) }}</span>
                </div>
                <div class="hr-flex-1">
                  <h6 class="hr-mb-1" style="font-size: 15px; font-weight: 600; color: #1e293b; margin-top: 0;">{{ meeting.title }}</h6>
                  <div class="hr-small hr-text-muted hr-flex-center hr-gap-2">
                    <span><i class="bx bx-time"></i> {{ formatMeetingTime(meeting) }}</span>
                    <span><i class="bx bx-map"></i> {{ meeting.location || 'N/A' }}</span>
                  </div>
                </div>
                <div>
                  <span class="hr-badge" :class="getMeetingTypeColor(meeting.meetingType)">{{ meeting.meetingType }}</span>
                </div>
              </div>
            </div>
            <div v-else class="hr-meet-empty">
              <i class="bx bx-calendar-x"></i>
              <p>No upcoming meetings</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Meetings list table -->
      <div class="hr-chart-card hr-premium-card hr-mb-4">
        <div class="hr-chart-header">
          <h5 class="hr-chart-title"><i class="bx bx-list-ul"></i> All Meetings</h5>
        </div>
        <div class="hr-chart-body" style="padding: 0;">
          <div v-if="loading" class="hr-loading-state hr-py-5">
            <div class="hr-spinner"></div>
            <p>Loading meetings data...</p>
          </div>
          
          <div v-else-if="!meetings.length" class="hr-meet-empty" style="margin: 20px; border: none; background: transparent;">
            <i class="bx bx-calendar-x"></i>
            <h5>No meetings found</h5>
            <p class="hr-text-muted">Try adjusting your filters or schedule a new meeting</p>
            <button @click="resetFilters" class="hr-btn hr-btn-outline-primary hr-mt-2">Reset Filters</button>
          </div>

          <div v-else class="hr-table-wrapper" style="padding: 0 20px;">
            <table class="hr-table-modern">
              <thead>
                <tr>
                  <th>Meeting</th>
                  <th>Type</th>
                  <th>Date & Time</th>
                  <th>Location</th>
                  <th>Organizer</th>
                  <th>Status</th>
                  <th class="hr-text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="meeting in meetings" :key="meeting.id">
                  <td>
                    <div class="hr-flex-center">
                      <div class="hr-avatar-initials hr-avatar-primary hr-me-3">
                        <i class="bx bx-calendar"></i>
                      </div>
                      <div>
                        <strong style="color: #1e293b; display: block; margin-bottom: 2px;">{{ meeting.title }}</strong>
                        <span class="hr-small hr-text-muted">{{ meeting.meetingCode }}</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span class="hr-badge" :class="getMeetingTypeColor(meeting.meetingType)">{{ meeting.meetingType }}</span>
                  </td>
                  <td>
                    <div style="font-size: 13px; color: #334155;">
                      <span class="hr-flex-center hr-gap-1 hr-mb-1"><i class="bx bx-calendar-event hr-text-muted"></i> {{ formatDate(meeting.startDate) }}</span>
                      <span v-if="meeting.startTime" class="hr-small hr-text-muted hr-flex-center hr-gap-1">
                        <i class="bx bx-time hr-text-muted"></i> {{ formatTime(meeting.startTime) }} - {{ formatTime(meeting.endTime) }}
                      </span>
                    </div>
                  </td>
                  <td style="font-size: 14px; color: #475569;">{{ meeting.location || 'N/A' }}</td>
                  <td style="font-size: 14px; color: #475569;">{{ meeting.organizer || 'N/A' }}</td>
                  <td>
                    <span class="hr-badge" :class="getStatusBadgeClass(meeting.status)">{{ meeting.status }}</span>
                  </td>
                  <td class="hr-text-center">
                    <div class="hr-flex-center hr-gap-2 hr-justify-center">
                      <button @click="viewMeetingDetails(meeting)" class="hr-btn hr-btn-icon hr-btn-info" title="View Details">
                        <i class="bx bx-show"></i>
                      </button>
                      <button @click="editMeeting(meeting)" class="hr-btn hr-btn-icon hr-btn-primary" title="Edit Meeting">
                        <i class="bx bx-edit-alt"></i>
                      </button>
                      <button v-if="meeting && meeting.status !== 'Canceled'" @click="cancelMeeting(meeting)" class="hr-btn hr-btn-icon hr-btn-danger" title="Cancel Meeting">
                        <i class="bx bx-x"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div v-if="meetings.length" class="hr-pagination-wrap hr-pb-3" style="padding-left: 20px; padding-right: 20px; padding-top: 10px;">
            <div class="hr-pagination-info">
              Showing <strong>{{ meetings.length }}</strong> of <strong>{{ pagination.totalMeetings }}</strong> meetings
            </div>
            <nav v-if="pagination.totalPages > 1" class="hr-pagination-nav">
              <button type="button" class="hr-page-btn" :disabled="pagination.currentPage <= 1" @click="changePage(pagination.currentPage - 1)">
                <i class="bx bx-chevron-left"></i>
              </button>
              <template v-for="page in getPageNumbers()" :key="page">
                <button v-if="page !== '...'" type="button" class="hr-page-btn" :class="{ active: page === pagination.currentPage }" @click="changePage(page)">
                  {{ page }}
                </button>
                <span v-else class="hr-page-btn hr-page-ellipsis">{{ page }}</span>
              </template>
              <button type="button" class="hr-page-btn" :disabled="pagination.currentPage >= pagination.totalPages" @click="changePage(pagination.currentPage + 1)">
                <i class="bx bx-chevron-right"></i>
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>

    <teleport to="body">
  <!-- Meeting Create/Edit Modal -->
  <div class="modal fade" id="meetingModal" tabindex="-1" aria-labelledby="meetingModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="meetingModalLabel">{{ isEditMode ? 'Edit Meeting' : 'Schedule New Meeting' }}</h5>
          <button type="button" class="btn-close" aria-label="Close" @click="closeMeetingModal"></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="submitMeeting">
            <div class="row g-3">
              <div class="col-md-12">
                <label class="form-label">Meeting Title <span class="text-danger">*</span></label>
                <input v-model="meetingForm.title" type="text" class="form-control" required>
              </div>

              <div class="col-md-6">
                <label class="form-label">Meeting Type <span class="text-danger">*</span></label>
                <select v-model="meetingForm.meetingType" class="form-select" required>
                  <option value="" disabled>Select Meeting Type</option>
                  <option v-for="type in meetingTypes" :key="type" :value="type">{{ type }}</option>
                </select>
              </div>

              <div class="col-md-12">
                <label class="form-label">Target Audience <span class="text-danger">*</span></label>
                <div class="d-flex flex-column gap-2">
                  <div class="form-check form-check-inline">
                    <input class="form-check-input" type="radio" v-model="meetingForm.targetAudience" id="meetingTargetAll" value="All Employees">
                    <label class="form-check-label" for="meetingTargetAll">All Employees</label>
                  </div>
                  <div class="form-check form-check-inline">
                    <input class="form-check-input" type="radio" v-model="meetingForm.targetAudience" id="meetingTargetDepartments" value="Specific Departments">
                    <label class="form-check-label" for="meetingTargetDepartments">Specific Departments</label>
                  </div>
                  <div class="form-check form-check-inline">
                    <input class="form-check-input" type="radio" v-model="meetingForm.targetAudience" id="meetingTargetEmployees" value="Specific Employees">
                    <label class="form-check-label" for="meetingTargetEmployees">Specific Employees</label>
                  </div>
                </div>
              </div>

              <div v-if="meetingForm.targetAudience === 'Specific Departments'" class="col-md-12">
                <label class="form-label">Select Departments <span class="text-danger">*</span></label>
                <div class="card p-3">
                  <div class="department-selection">
                    <div v-for="dept in departments" :key="dept.id" class="form-check mb-2">
                      <input
                          :id="`meeting-dept-${dept.id}`"
                          type="checkbox"
                          :value="dept.id"
                          v-model="meetingForm.departments"
                          class="form-check-input"
                      >
                      <label :for="`meeting-dept-${dept.id}`" class="form-check-label">{{ dept.departmentName }}</label>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="meetingForm.targetAudience === 'Specific Employees'" class="col-md-12">
                <label class="form-label">Select Employees <span class="text-danger">*</span></label>
                <div class="card p-3">
                  <select v-model="meetingForm.participants" class="form-select" multiple size="6">
                    <option v-for="employee in employees" :key="employee.id" :value="employee.id">
                      {{ employee.firstName }} {{ employee.lastName }} ({{ employee.departmentName }})
                    </option>
                  </select>
                  <small class="text-muted mt-2">Hold Ctrl/Cmd to select multiple employees</small>
                </div>
              </div>

              <div class="col-md-6">
                <label class="form-label">Date <span class="text-danger">*</span></label>
                <input v-model="meetingForm.startDate" type="date" class="form-control" required>
              </div>

              <div class="col-md-6">
                <label class="form-label">Duration (minutes) <span class="text-danger">*</span></label>
                <input v-model="meetingForm.duration" type="number" min="15" step="15" class="form-control" required>
              </div>

              <div class="col-md-6">
                <label class="form-label">Start Time <span class="text-danger">*</span></label>
                <input v-model="meetingForm.startTime" type="time" class="form-control" required>
              </div>

              <div class="col-md-6">
                <label class="form-label">End Time</label>
                <input v-model="meetingForm.endTime" type="time" class="form-control" disabled>
              </div>

              <div class="col-md-12">
                <label class="form-label">Location</label>
                <input v-model="meetingForm.location" type="text" class="form-control">
              </div>

              <div class="col-md-6">
                <label class="form-label">Virtual Platform</label>
                <select v-model="meetingForm.virtualMeetingPlatform" class="form-select">
                  <option value="">None</option>
                  <option value="Google Meet">Google Meet</option>
                  <option value="Zoom">Zoom</option>
                  <option value="Microsoft Teams">Microsoft Teams</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div class="col-md-6">
                <label class="form-label">Virtual Meeting Link</label>
                <input 
                  v-if="!meetingForm.virtualMeetingPlatform || meetingForm.virtualMeetingPlatform === 'Other' || !isIntegrationConnected(meetingForm.virtualMeetingPlatform)" 
                  v-model="meetingForm.virtualMeetingLink" 
                  type="text" 
                  class="form-control" 
                  :disabled="!meetingForm.virtualMeetingPlatform"
                  placeholder="Enter meeting link"
                >
                <input 
                  v-else 
                  type="text" 
                  class="form-control" 
                  disabled 
                  placeholder="Link will be auto-generated by StaffSync"
                >
                <small v-if="meetingForm.virtualMeetingPlatform && meetingForm.virtualMeetingPlatform !== 'Other' && !isIntegrationConnected(meetingForm.virtualMeetingPlatform)" class="text-warning mt-1">
                  <i class="mdi mdi-alert"></i> Account not connected. Link will not be auto-generated.
                </small>
              </div>

              <div class="col-md-6">
                <label class="form-label">Priority</label>
                <select v-model="meetingForm.priority" class="form-select">
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                </select>
              </div>

              <div class="col-md-6">
                <label class="form-label">Status</label>
                <select v-model="meetingForm.status" class="form-select">
                  <option value="Scheduled">Scheduled</option>
                  <option value="Postponed">Postponed</option>
                  <option value="Canceled">Canceled</option>
                  <option value="Completed">Completed</option>
                </select>
              </div>

              <div class="col-md-12">
                <label class="form-label">Agenda</label>
                <textarea v-model="meetingForm.agenda" class="form-control" rows="3"></textarea>
              </div>



              <div class="col-md-12">
                <label class="form-label">Attachments</label>
                <input type="file" class="form-control" multiple>
              </div>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeMeetingModal">Close</button>
          <button type="button" class="btn btn-primary" @click="submitMeeting">
            {{ isEditMode ? 'Update Meeting' : 'Schedule Meeting' }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Meeting Details Modal -->
  <div class="modal fade" id="meetingDetailsModal" tabindex="-1" aria-labelledby="meetingDetailsModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="meetingDetailsModalLabel">Meeting Details</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div v-if="selectedMeeting">
            <div class="row mb-4">
              <div class="col-md-8">
                <h4 class="font-size-16">{{ selectedMeeting.title }}</h4>
                <p class="text-muted mb-1">
                  <span class="badge rounded-pill" :class="getMeetingTypeColor(selectedMeeting.meetingType)">
                    {{ selectedMeeting.meetingType }}
                  </span>
                </p>
                <p class="text-muted mb-0">Meeting Code: {{ selectedMeeting.meetingCode }}</p>
              </div>
              <div class="col-md-4 text-md-end">
                <span class="badge" :class="getStatusBadgeClass(selectedMeeting.status)">
                  {{ selectedMeeting.status }}
                </span>
              </div>
            </div>

            <div class="row">
              <div class="col-md-6">
                <div class="card border">
                  <div class="card-header bg-light">
                    <h6 class="mb-0">Meeting Information</h6>
                  </div>
                  <div class="card-body">
                    <ul class="list-group list-group-flush">
                      <li class="list-group-item">
                        <div class="d-flex justify-content-between">
                          <span><i class="mdi mdi-calendar me-2"></i>Date:</span>
                          <span class="text-muted">{{ formatDate(selectedMeeting.startDate) }}</span>
                        </div>
                      </li>
                      <li class="list-group-item" v-if="selectedMeeting.startTime">
                        <div class="d-flex justify-content-between">
                          <span><i class="mdi mdi-clock-outline me-2"></i>Time:</span>
                          <span class="text-muted">{{ formatTime(selectedMeeting.startTime) }} - {{ formatTime(selectedMeeting.endTime) }}</span>
                        </div>
                      </li>
                      <li class="list-group-item">
                        <div class="d-flex justify-content-between">
                          <span><i class="mdi mdi-map-marker me-2"></i>Location:</span>
                          <span class="text-muted">{{ selectedMeeting.location || 'N/A' }}</span>
                        </div>
                      </li>
                      <li class="list-group-item" v-if="selectedMeeting.virtualMeetingLink">
                        <div class="d-flex justify-content-between">
                          <span><i class="mdi mdi-video me-2"></i>Virtual Link:</span>
                          <a :href="selectedMeeting.virtualMeetingLink" target="_blank">Join Meeting</a>
                        </div>
                      </li>
                      <li class="list-group-item">
                        <div class="d-flex justify-content-between">
                          <span><i class="mdi mdi-account me-2"></i>Organizer:</span>
                          <span class="text-muted">{{ selectedMeeting.organizer || 'N/A' }}</span>
                        </div>
                      </li>
                      <li class="list-group-item">
                        <div class="d-flex justify-content-between">
                          <span><i class="mdi mdi-flag me-2"></i>Priority:</span>
                          <span class="text-muted">{{ selectedMeeting.priority }}</span>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="card border h-100">
                  <div class="card-header bg-light">
                    <h6 class="mb-0">Agenda</h6>
                  </div>
                  <div class="card-body">
                    <p v-if="selectedMeeting.agenda">{{ selectedMeeting.agenda }}</p>
                    <p v-else class="text-muted text-center">No agenda provided</p>
                  </div>
                </div>
              </div>
            </div>

            <div class="row mt-4">
              <div class="col-md-12">
                <div class="card border">
                  <div class="card-header bg-light">
                    <h6 class="mb-0">Participants</h6>
                  </div>
                  <div class="card-body">
                    <div v-if="selectedMeeting.participantsList && selectedMeeting.participantsList.length > 0">
                      <div class="table-responsive">
                        <table class="table table-sm table-striped">
                          <thead>
                          <tr>
                            <th>Name</th>
                            <th>Department</th>
                            <th>Response</th>
                          </tr>
                          </thead>
                          <tbody>
                          <tr v-for="(participant, index) in selectedMeeting.participantsList" :key="index">
                            <td>{{ participant.name }}</td>
                            <td>{{ participant.department || 'N/A' }}</td>
                            <td>
                                  <span class="badge" :class="getResponseBadgeClass(participant.response)">
                                    {{ participant.response || 'Pending' }}
                                  </span>
                            </td>
                          </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <p v-else class="text-muted text-center">No participants added</p>
                  </div>
                </div>
              </div>
            </div>


            <!-- Meeting Minutes Section -->
            <div class="row mt-4" v-if="selectedMeeting && selectedMeeting.minutes">
              <div class="col-md-12">
                <div class="card border">
                  <div class="card-header bg-light d-flex justify-content-between align-items-center">
                    <h6 class="mb-0">Meeting Minutes</h6>
                    <span class="text-muted small" v-if="selectedMeeting.minutes.createdAt">
          <i class="mdi mdi-clock-outline me-1"></i>{{ formatDateTime(selectedMeeting.minutes.createdAt) }}
        </span>
                  </div>
                  <div class="card-body">
                    <!-- Minutes Content -->
                    <div class="mb-4">
                      <p class="mb-0 text-pre-wrap">{{ selectedMeeting.minutes.content }}</p>
                    </div>

                    <!-- Action Items -->
                    <div v-if="selectedMeeting.minutes.actionItems && selectedMeeting.minutes.actionItems.length > 0" class="mt-4">
                      <h6 class="card-subtitle mb-3 text-muted">Action Items</h6>
                      <div class="table-responsive">
                        <table class="table table-sm table-bordered">
                          <thead class="table-light">
                          <tr>
                            <th>Action Item</th>
                            <th>Assigned To</th>
                            <th>Due Date</th>
                            <th>Status</th>
                          </tr>
                          </thead>
                          <tbody>
                          <tr v-for="(item, index) in selectedMeeting.minutes.actionItems" :key="index">
                            <td>{{ item.description }}</td>
                            <td>{{ item.assignedTo }}</td>
                            <td>{{ formatDate(item.dueDate) }}</td>
                            <td>
                  <span class="badge" :class="getActionItemStatusClass(item.status || 'Pending')">
                    {{ item.status || 'Pending' }}
                  </span>
                            </td>
                          </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>




          </div>

          <!-- Meeting Actions -->
          <div class="row mt-4">
            <div class="col-12">
              <div class="d-flex justify-content-end">



                <!-- Add this button to the Meeting Actions section in the meeting details modal -->
                <button
                    v-if="selectedMeeting && (selectedMeeting.status === 'Completed' || selectedMeeting.status === 'Scheduled')"
                    @click="openMeetingMinutesModal(selectedMeeting)"
                    class="btn btn-info me-2">
                  <i class="mdi mdi-file-document-edit me-1"></i> {{ selectedMeeting.minutes ? 'Edit Minutes' : 'Add Minutes' }}
                </button>

                <!-- Status actions based on current status -->
                <button
                    v-if="selectedMeeting && selectedMeeting.status === 'Scheduled'"
                    @click="completeMeeting(selectedMeeting)"
                    class="btn btn-success me-2">
                  <i class="mdi mdi-check-circle me-1"></i> Mark as Completed
                </button>
                <button
                    v-if="selectedMeeting && selectedMeeting.status !== 'Canceled'"
                    @click="cancelMeeting(selectedMeeting)"
                    class="btn btn-danger me-2">
                  <i class="mdi mdi-close-circle me-1"></i> Cancel Meeting
                </button>

                <button
                    @click="editMeeting(selectedMeeting)"
                    class="btn btn-primary me-2">
                  <i class="mdi mdi-pencil me-1"></i> Edit
                </button>
                <button
                    @click="exportMeetingDetails(selectedMeeting)"
                    class="btn btn-info">
                  <i class="mdi mdi-download me-1"></i> Export
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
      </div>
    </div>
  </div>


  <!-- Cancel Meeting Modal -->
  <div class="modal fade" id="cancelMeetingModal" tabindex="-1" aria-labelledby="cancelMeetingModalLabel" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="cancelMeetingModalLabel">Cancel Meeting</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <p>Are you sure you want to cancel this meeting?</p>
          <div class="mb-3">
            <label for="cancellationReason" class="form-label">Reason for cancellation</label>
            <textarea
                id="cancellationReason"
                v-model="cancellationReason"
                class="form-control"
                rows="3"
                placeholder="Please provide a reason for cancellation..."></textarea>
          </div>
          <div class="form-check mb-3">
            <input
                class="form-check-input"
                type="checkbox"
                v-model="notifyParticipants"
                id="notifyParticipantsCheck">
            <label class="form-check-label" for="notifyParticipantsCheck">
              Notify all participants about cancellation
            </label>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
          <button type="button" class="btn btn-danger" @click="confirmCancelMeeting">
            <i class="mdi mdi-close-circle me-1"></i> Cancel Meeting
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Meeting Minutes Modal -->
  <div class="modal fade" id="meetingMinutesModal" tabindex="-1" aria-labelledby="meetingMinutesModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="meetingMinutesModalLabel">Add Meeting Minutes</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="submitMeetingMinutes">
            <div class="mb-3">
              <label class="form-label">Meeting Minutes <span class="text-danger">*</span></label>
              <textarea v-model="minutesForm.content" class="form-control" rows="8" required></textarea>
            </div>

            <div class="mb-3">
              <label class="form-label">Action Items</label>
              <div v-for="(item, index) in minutesForm.actionItems" :key="index" class="d-flex mb-2">
                <input v-model="item.description" type="text" class="form-control me-2" placeholder="Action item description">
                <input v-model="item.assignedTo" type="text" class="form-control me-2" placeholder="Assigned to">
                <input v-model="item.dueDate" type="date" class="form-control me-2">
                <button type="button" class="btn btn-danger btn-sm" @click="removeActionItem(index)">
                  <i class="mdi mdi-delete"></i>
                </button>
              </div>
              <button type="button" class="btn btn-sm btn-outline-primary mt-2" @click="addActionItem">
                <i class="mdi mdi-plus me-1"></i> Add Action Item
              </button>
            </div>

            <div class="form-check mb-3">
              <input class="form-check-input" type="checkbox" v-model="minutesForm.notifyParticipants" id="notifyParticipantsMinutes">
              <label class="form-check-label" for="notifyParticipantsMinutes">
                Send meeting minutes to all participants
              </label>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
          <button type="button" class="btn btn-primary" @click="submitMeetingMinutes">
            <i class="mdi mdi-content-save me-1"></i> Save Minutes
          </button>
        </div>
      </div>
    </div>
  </div>
  </teleport>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import { authUrl, execute } from "@/api";
import _ from 'lodash';
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import * as bootstrap from 'bootstrap';

export default {
  name: "HRMeetings",
  components: { HrPageLayout },
  data() {
    return {
      loading: false,
      meetings: [],
      upcomingMeetings: [],
      departments: [],
      employees: [],
      meetingTypes: ['Team Meeting', 'One-on-One', 'Board Meeting', 'Interview', 'Conference Call', 'Training', 'Workshop', 'Brainstorming', 'Review', 'Other'],
      filters: {
        departmentId: "",
        meetingType: "",
        startDate: this.getDateOffset(-30), // Last 30 days
        endDate: this.getDateOffset(30), // Next 30 days
        search: "",
        page: 1,
        limit: 10
      },
      pagination: {
        currentPage: 1,
        totalPages: 1,
        totalMeetings: 0,
        limit: 10
      },
      metrics: {
        totalMeetings: 0,
        upcomingMeetings: 0,
        meetingsThisMonth: 0,
        todayMeetings: 0,
        typeCounts: {}
      },
      integrations: {},
      selectedMeeting: null,
      meetingForm: {
        title: "",
        agenda: "",
        meetingType: "",
        startDate: "",
        endDate: "",
        startTime: "",
        endTime: "",
        duration: 60,
        location: "",
        virtualMeetingLink: "",
        departmentId: "",
        participants: [],
        priority: "Medium",
        status: "Scheduled"
      },
      isEditMode: false,
      cancellationReason: "",
      notifyParticipants: true,
      minutesForm: {
        content: "",
        actionItems: [],
        notifyParticipants: true
      },
    };
  },
  computed: {
    today() {
      return new Date().toLocaleDateString();
    },
    currentMonthName() {
      return new Date().toLocaleString('default', { month: 'long' });
    }
  },
  mounted() {
    this.getDepartments();
    this.getEmployees();
    this.getMeetings();
    this.loadIntegrations();
  },
  methods: {
    getDateOffset(days) {
      const date = new Date();
      date.setDate(date.getDate() + days);
      return date.toISOString().split('T')[0];
    },

    getDepartments() {
      const data = new FormData();
      data.append("function", "getDepartments");
      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.departments = res.data.data.departments || [];
            } else {
              console.error("Error fetching departments:", res.data.message);
              this.showToast("error", "Failed to load departments");
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching departments");
          });
    },

    getEmployees() {
      const data = new FormData();
      data.append("function", "getEmployees");
      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.employees = res.data.data.employees || [];
            } else {
              console.error("Error fetching employees:", res.data.message);
              this.showToast("error", "Failed to load employees");
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching employees");
          });
    },

    getMeetings() {
      this.loading = true;

      const data = new FormData();
      data.append("function", "getMeetings");
      data.append("startDate", this.filters.startDate);
      data.append("endDate", this.filters.endDate);
      data.append("page", this.filters.page);
      data.append("limit", this.filters.limit);

      if (this.filters.departmentId) {
        data.append("departmentId", this.filters.departmentId);
      }

      if (this.filters.meetingType) {
        data.append("meetingType", this.filters.meetingType);
      }

      if (this.filters.search) {
        data.append("search", this.filters.search);
      }

      execute(data, authUrl)
          .then((res) => {
            this.loading = false;

            if (res.data.success) {
              this.meetings = res.data.data.meetings || [];
              this.metrics = res.data.data.metrics || {};
              this.pagination = res.data.data.pagination || {
                currentPage: 1,
                totalPages: 1,
                totalMeetings: 0,
                limit: 10
              };

              // Get upcoming meetings (next 7 days)
              const today = new Date();
              const nextWeek = new Date();
              nextWeek.setDate(today.getDate() + 7);

              this.upcomingMeetings = this.meetings
                  .filter(meeting => {
                    const meetingDate = new Date(meeting.startDate);
                    return meetingDate >= today && meetingDate <= nextWeek && meeting.status !== 'Canceled';
                  })
                  .slice(0, 5); // Top 5 upcoming meetings
            } else {
              console.error("Error fetching meetings:", res.data.message);
              this.showToast("error", "Failed to load meetings data: " + res.data.message);
              this.meetings = [];
              this.metrics = {};
            }
          })
          .catch((error) => {
            this.loading = false;
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching meetings");
            this.meetings = [];
            this.metrics = {};
          });
    },


    refreshData() {
      this.getMeetings();
    },

    resetFilters() {
      this.filters = {
        departmentId: "",
        meetingType: "",
        startDate: this.getDateOffset(-30),
        endDate: this.getDateOffset(30),
        search: "",
        page: 1,
        limit: 10
      };
      this.getMeetings();
    },

    changePage(page) {
      if (page < 1 || page > this.pagination.totalPages || page === this.filters.page) {
        return;
      }

      this.filters.page = page;
      this.getMeetings();
    },

    getPageNumbers() {
      const totalPages = this.pagination.totalPages || 1;
      const currentPage = this.pagination.currentPage || 1;

      if (totalPages <= 5) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
      }

      const pages = [1];

      if (currentPage <= 3) {
        pages.push(2, 3);
        if (totalPages > 3) pages.push('...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push('...', totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push('...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }

      return pages;
    },

    createMeeting() {
      this.isEditMode = false;
      this.meetingForm = {
        title: "",
        agenda: "",
        meetingType: "",
        startDate: new Date().toISOString().split('T')[0], // Today's date
        duration: 60, // Default to 60 minutes
        startTime: "",
        endTime: "",
        location: "",
        virtualMeetingPlatform: "",
        virtualMeetingLink: "",
        targetAudience: "All Employees",
        departments: [],
        participants: [],
        priority: "Medium",
        status: "Scheduled"
      };
      this.$nextTick(() => {
        const el = document.getElementById('meetingModal');
        if (el && typeof bootstrap !== 'undefined' && bootstrap.Modal) {
          const modal = bootstrap.Modal.getInstance(el) || new bootstrap.Modal(el);
          modal.show();
        }
      });
    },

    editMeeting(meeting) {
      this.isEditMode = true;
      this.selectedMeeting = meeting;

      // Close details modal if open
      const detailsModal = bootstrap.Modal.getInstance(document.getElementById('meetingDetailsModal'));
      if (detailsModal) {
        detailsModal.hide();
      }

      // Calculate duration based on start and end time
      let duration = 60; // Default
      if (meeting.startTime && meeting.endTime) {
        const start = new Date(`2000-01-01T${meeting.startTime}`);
        const end = new Date(`2000-01-01T${meeting.endTime}`);
        duration = (end - start) / 60000; // Convert milliseconds to minutes
      }

      // Prepare form data
      this.meetingForm = {
        id: meeting.id,
        title: meeting.title,
        agenda: meeting.agenda || "",
        meetingType: meeting.meetingType,
        startDate: meeting.startDate,
        duration: duration,
        startTime: meeting.startTime || "",
        endTime: meeting.endTime || "",
        location: meeting.location || "",
        virtualMeetingPlatform: meeting.virtualMeetingPlatform || "",
        virtualMeetingLink: meeting.virtualMeetingLink || "",
        targetAudience: meeting.targetAudience || "All Employees",
        departments: meeting.departmentsList ? meeting.departmentsList : [],
        participants: meeting.participantsList ? meeting.participantsList.map(att => att.employeeId) : [],
        priority: meeting.priority || "Medium",
        status: meeting.status || "Scheduled"
      };

      // Open the meeting modal
      this.$nextTick(() => {
        const el = document.getElementById('meetingModal');
        if (el && typeof bootstrap !== 'undefined' && bootstrap.Modal) {
          const modal = bootstrap.Modal.getInstance(el) || new bootstrap.Modal(el);
          modal.show();
        }
      });
    },

    // Calculate end time when start time or duration changes
    updateEndTime() {
      if (this.meetingForm.startTime && this.meetingForm.duration) {
        const [hours, minutes] = this.meetingForm.startTime.split(':').map(Number);
        const startDate = new Date();
        startDate.setHours(hours, minutes, 0);

        const endDate = new Date(startDate.getTime() + this.meetingForm.duration * 60000);
        const endHours = endDate.getHours().toString().padStart(2, '0');
        const endMinutes = endDate.getMinutes().toString().padStart(2, '0');

        this.meetingForm.endTime = `${endHours}:${endMinutes}`;
      } else {
        this.meetingForm.endTime = '';
      }
    },

    async loadIntegrations() {
      try {
        const data = new FormData();
        data.append("function", "getUserIntegrations");
        const res = await execute(data, authUrl);
        if (res && res.data && res.data.success) {
          this.integrations = res.data.data || {};
        }
      } catch (error) {
        console.error("Failed to load integrations:", error);
      }
    },

    isIntegrationConnected(platform) {
      if (!platform || platform === 'Other') return false;
      return !!this.integrations[platform]?.connected;
    },

    submitMeeting() {
      // Validate required fields
      if (!this.meetingForm.title || !this.meetingForm.startDate || !this.meetingForm.meetingType || !this.meetingForm.startTime) {
        this.showToast("error", "Please fill in all required fields");
        return;
      }

      // Update end time based on start time and duration
      this.updateEndTime();

      // Prepare form data
      const data = new FormData();

      if (this.isEditMode) {
        data.append("function", "updateMeeting");
        data.append("meetingId", this.meetingForm.id);
      } else {
        data.append("function", "createMeeting");
      }

      // Add all form fields
      data.append("title", this.meetingForm.title);
      data.append("agenda", this.meetingForm.agenda);
      data.append("meetingType", this.meetingForm.meetingType);
      data.append("startDate", this.meetingForm.startDate);
      data.append("startTime", this.meetingForm.startTime);
      data.append("endTime", this.meetingForm.endTime);
      data.append("location", this.meetingForm.location);
      data.append("virtualMeetingPlatform", this.meetingForm.virtualMeetingPlatform);
      data.append("virtualMeetingLink", this.meetingForm.virtualMeetingLink);
      data.append("targetAudience", this.meetingForm.targetAudience);
      data.append("priority", this.meetingForm.priority);
      data.append("status", this.meetingForm.status);

      // Handle departments
      if (this.meetingForm.targetAudience === 'Specific Departments' && this.meetingForm.departments.length > 0) {
        data.append("departments", JSON.stringify(this.meetingForm.departments));
      } else {
        data.append("departments", "[]");
      }

      // Handle participants
      if (this.meetingForm.targetAudience === 'Specific Employees' && this.meetingForm.participants.length > 0) {
        // Create participants array of objects
        const participantsArray = this.meetingForm.participants.map(empId => {
          const employee = this.employees.find(emp => emp.id === empId);
          return {
            employeeId: empId,
            name: employee ? `${employee.firstName} ${employee.lastName}` : 'Unknown'
          };
        });
        data.append("participants", JSON.stringify(participantsArray));
      } else {
        data.append("participants", "[]");
      }

      // Send the request
      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.showToast("success", this.isEditMode ? "Meeting updated successfully" : "Meeting created successfully");

              // Close the modal
              this.closeMeetingModal();

              // Refresh the meetings data
              this.getMeetings();
            } else {
              console.error("Error saving meeting:", res.data.message);
              this.showToast("error", `Failed to ${this.isEditMode ? 'update' : 'create'} meeting: ${res.data.message}`);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", `Network error when ${this.isEditMode ? 'updating' : 'creating'} meeting`);
          })
          .finally(() => {
            this.loading = false;
          });
    },

    viewMeetingDetails(meeting) {
      this.selectedMeeting = null;
      this.getMeetingDetails(meeting.id);
    },

    cancelMeeting(meeting) {
      this.selectedMeeting = meeting;
      this.cancellationReason = "";
      this.notifyParticipants = true;

      // Close any other open modals
      const detailsModal = bootstrap.Modal.getInstance(document.getElementById('meetingDetailsModal'));
      if (detailsModal) {
        detailsModal.hide();
      }

      // Open the cancel meeting modal
      const el = document.getElementById('cancelMeetingModal');
      const modal = bootstrap.Modal.getInstance(el) || new bootstrap.Modal(el);
      modal.show();
    },

    closeMeetingModal() {
      const el = document.getElementById('meetingModal');
      if (el && typeof bootstrap !== 'undefined' && bootstrap.Modal) {
        const instance = bootstrap.Modal.getInstance ? bootstrap.Modal.getInstance(el) : null;
        if (instance) {
          instance.hide();
        } else {
          const modal = new bootstrap.Modal(el);
          modal.hide();
        }
      }
    },

    confirmCancelMeeting() {
      if (!this.selectedMeeting) return;

      const data = new FormData();
      data.append("function", "cancelMeeting");
      data.append("meetingId", this.selectedMeeting.id);
      data.append("reason", this.cancellationReason);
      data.append("notifyParticipants", this.notifyParticipants ? 1 : 0);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.showToast("success", "Meeting canceled successfully");

              // Close the modal
              const modal = bootstrap.Modal.getInstance(document.getElementById('cancelMeetingModal'));
              if (modal) {
                modal.hide();
              }

              // Refresh the meetings data
              this.getMeetings();
            } else {
              console.error("Error canceling meeting:", res.data.message);
              this.showToast("error", "Failed to cancel meeting: " + res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when canceling meeting");
          });
    },

    completeMeeting(meeting) {
      const data = new FormData();
      data.append("function", "completeMeeting");
      data.append("meetingId", meeting.id);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.showToast("success", "Meeting marked as completed");

              // Close the details modal
              const modal = bootstrap.Modal.getInstance(document.getElementById('meetingDetailsModal'));
              if (modal) {
                modal.hide();
              }

              // Refresh the meetings data
              this.getMeetings();
            } else {
              console.error("Error completing meeting:", res.data.message);
              this.showToast("error", "Failed to complete meeting: " + res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when completing meeting");
          });
    },

    exportMeetings() {
      const data = new FormData();
      data.append("function", "exportMeetingsReport");
      data.append("startDate", this.filters.startDate);
      data.append("endDate", this.filters.endDate);

      if (this.filters.departmentId) {
        data.append("departmentId", this.filters.departmentId);
      }

      if (this.filters.meetingType) {
        data.append("meetingType", this.filters.meetingType);
      }

      // Use vanilla form submission for file download
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = authUrl;

      // Append each key-value pair to the form
      for (const pair of data.entries()) {
        const hiddenField = document.createElement('input');
        hiddenField.type = 'hidden';
        hiddenField.name = pair[0];
        hiddenField.value = pair[1];
        form.appendChild(hiddenField);
      }

      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    },

    exportMeetingDetails(meeting) {
      if (!meeting) return;

      const data = new FormData();
      data.append("function", "exportMeetingDetails");
      data.append("meetingId", meeting.id);

      // Use vanilla form submission for file download
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = authUrl;

      // Append each key-value pair to the form
      for (const pair of data.entries()) {
        const hiddenField = document.createElement('input');
        hiddenField.type = 'hidden';
        hiddenField.name = pair[0];
        hiddenField.value = pair[1];
        form.appendChild(hiddenField);
      }

      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    },

    addActionItem() {
      this.minutesForm.actionItems.push({
        description: "",
        assignedTo: "",
        dueDate: ""
      });
    },

    removeActionItem(index) {
      this.minutesForm.actionItems.splice(index, 1);
    },
    openMeetingMinutesModal(meeting) {
      this.selectedMeeting = meeting;

      // Reset the minutes form
      this.minutesForm = {
        content: "",
        actionItems: [],
        notifyParticipants: true
      };

      // Check if there are existing minutes to load them
      if (meeting.minutes) {
        this.minutesForm.content = meeting.minutes.content || "";
        this.minutesForm.actionItems = meeting.minutes.actionItems || [];
      }

      // Close details modal if open
      const detailsModal = bootstrap.Modal.getInstance(document.getElementById('meetingDetailsModal'));
      if (detailsModal) {
        detailsModal.hide();
      }

      // Open the minutes modal
      const el = document.getElementById('meetingMinutesModal');
      const modal = bootstrap.Modal.getInstance(el) || new bootstrap.Modal(el);
      modal.show();
    },

    getMeetingDetails(meetingId) {
      const data = new FormData();
      data.append("function", "getMeetingDetails");
      data.append("meetingId", meetingId);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.selectedMeeting = res.data.data.selectedMeeting || res.data.data.meetingDetails;

              // Process meeting notes if available (convert from JSON string if needed)
              if (this.selectedMeeting && this.selectedMeeting.meetingNotes) {
                try {
                  // If it's a JSON string, parse it
                  if (typeof this.selectedMeeting.meetingNotes === 'string') {
                    this.selectedMeeting.minutes = JSON.parse(this.selectedMeeting.meetingNotes);
                  } else {
                    // It's already an object
                    this.selectedMeeting.minutes = this.selectedMeeting.meetingNotes;
                  }

                  // Ensure proper structure - initialize with defaults if properties are missing
                  this.selectedMeeting.minutes = {
                    content: this.selectedMeeting.minutes.content || '',
                    createdAt: this.selectedMeeting.minutes.addedAt || this.selectedMeeting.updatedAt || new Date().toISOString(),
                    actionItems: this.selectedMeeting.minutes.actionItems || []
                  };

                  // Format action item dates if present
                  if (this.selectedMeeting.minutes.actionItems && this.selectedMeeting.minutes.actionItems.length > 0) {
                    this.selectedMeeting.minutes.actionItems.forEach(item => {
                      // Check for overdue items and update status
                      if (item.dueDate && new Date(item.dueDate) < new Date() && item.status !== 'Completed') {
                        item.status = 'Overdue';
                      }
                    });
                  }
                } catch (error) {
                  console.error("Error parsing meeting notes:", error);
                  // Initialize with an empty structure to prevent null reference errors
                  this.selectedMeeting.minutes = {
                    content: '',
                    createdAt: new Date().toISOString(),
                    actionItems: []
                  };
                }
              } else {
                // Initialize with an empty structure to prevent null reference errors
                this.selectedMeeting.minutes = {
                  content: '',
                  createdAt: new Date().toISOString(),
                  actionItems: []
                };
              }

              // Open the meeting details modal
              const el = document.getElementById('meetingDetailsModal');
              const modal = bootstrap.Modal.getInstance(el) || new bootstrap.Modal(el);
              modal.show();
            } else {
              console.error("Error fetching meeting details:", res.data.message);
              this.showToast("error", "Failed to load meeting details: " + res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching meeting details");
          });
    },


    submitMeetingMinutes() {
      // Validate required fields
      if (!this.minutesForm.content) {
        this.showToast("error", "Please enter meeting minutes content");
        return;
      }

      const data = new FormData();
      data.append("function", "saveMeetingMinutes");
      data.append("meetingId", this.selectedMeeting.id);
      data.append("content", this.minutesForm.content);
      data.append("notifyParticipants", this.minutesForm.notifyParticipants ? 1 : 0);

      // Create the meeting notes object in the expected format
      const meetingNotes = {
        content: this.minutesForm.content,
        actionItems: this.minutesForm.actionItems || [],
        addedBy: this.selectedMeeting.organizer || "USER", // Use meeting organizer or default
        addedAt: new Date().toISOString().replace('T', ' ').substring(0, 19) // Format: YYYY-MM-DD HH:MM:SS
      };

      data.append("meetingNotes", JSON.stringify(meetingNotes));

      if (this.minutesForm.actionItems.length > 0) {
        data.append("actionItems", JSON.stringify(this.minutesForm.actionItems));
      }

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.showToast("success", "Meeting minutes saved successfully");

              // Close the modal
              const modal = bootstrap.Modal.getInstance(document.getElementById('meetingMinutesModal'));
              if (modal) {
                modal.hide();
              }

              // Refresh meeting details
              this.getMeetingDetails(this.selectedMeeting.id);
            } else {
              console.error("Error saving meeting minutes:", res.data.message);
              this.showToast("error", "Failed to save meeting minutes: " + res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when saving meeting minutes");
          });
    },

    // Helper methods
    viewMeetingMinutes(meeting) {
      this.selectedMeeting = meeting;

      // You could either show a dedicated modal for viewing minutes
      // or just scroll to the minutes section in the details modal
      const detailsModal = bootstrap.Modal.getInstance(document.getElementById('meetingDetailsModal'));
      if (!detailsModal) {
        this.viewMeetingDetails(meeting);
      }
    },

    getActionItemStatusClass(status) {
      const statusMap = {
        'Completed': 'bg-success',
        'In Progress': 'bg-warning',
        'Pending': 'bg-secondary',
        'Overdue': 'bg-danger',
        'Not Started': 'bg-info'
      };

      return statusMap[status] || 'bg-secondary';
    },

    formatTime(timeString) {
      if (!timeString) return '';

      // If the time string is already in 12-hour format
      if (timeString.includes('AM') || timeString.includes('PM')) {
        return timeString;
      }

      // Convert 24-hour format to 12-hour format
      const [hours, minutes] = timeString.split(':');
      let hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      hour = hour % 12;
      hour = hour ? hour : 12; // the hour '0' should be '12'
      return `${hour}:${minutes} ${ampm}`;
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';

      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    formatDateTime(dateTimeString) {
      if (!dateTimeString) return 'N/A';

      const date = new Date(dateTimeString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    },

    formatDateMonth(dateString) {
      if (!dateString) return '';

      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { month: 'short' });
    },

    formatDateDay(dateString) {
      if (!dateString) return '';

      const date = new Date(dateString);
      return date.getDate();
    },

    formatMeetingTime(meeting) {
      if (!meeting.startTime) return 'No Time Specified';

      return `${this.formatTime(meeting.startTime)}${meeting.endTime ? ' - ' + this.formatTime(meeting.endTime) : ''}`;
    },

    getMeetingTypeColor(type) {
      if (!type) return 'bg-secondary';

      const typeMap = {
        'Team Meeting': 'bg-success',
        'One-on-One': 'bg-info',
        'Board Meeting': 'bg-primary',
        'Interview': 'bg-warning',
        'Conference Call': 'bg-purple',
        'Training': 'bg-indigo',
        'Workshop': 'bg-teal',
        'Brainstorming': 'bg-pink',
        'Review': 'bg-orange',
        'Other': 'bg-secondary'
      };

      return typeMap[type] || 'bg-secondary';
    },

    getStatusBadgeClass(status) {
      if (!status) return 'bg-secondary';

      const statusMap = {
        'Scheduled': 'bg-success',
        'Canceled': 'bg-danger',
        'Completed': 'bg-info',
        'In Progress': 'bg-warning',
        'Postponed': 'bg-warning'
      };

      return statusMap[status] || 'bg-secondary';
    },

    getResponseBadgeClass(response) {
      if (!response) return 'bg-secondary';

      const responseMap = {
        'Accepted': 'bg-success',
        'Declined': 'bg-danger',
        'Tentative': 'bg-warning',
        'Pending': 'bg-secondary'
      };

      return responseMap[response] || 'bg-secondary';
    },

    getMeetingTypePercentage(type) {
      if (!this.metrics.typeCounts || !this.metrics.totalMeetings) return 0;

      const count = this.metrics.typeCounts[type] || 0;
      return ((count / this.metrics.totalMeetings) * 100).toFixed(1);
    },

    showToast(type, message) {
      // Check if toast plugin exists before using it
      if (this.$toast) {
        this.$toast[type](message);
      } else {
        // Fallback to alert if toast plugin not available
        alert(message);
      }
    }
  },
  watch: {
    'filters.departmentId': _.debounce(function() {
      this.filters.page = 1; // Reset to first page
      this.getMeetings();
    }, 300),

    'filters.meetingType': _.debounce(function() {
      this.filters.page = 1; // Reset to first page
      this.getMeetings();
    }, 300),

    'filters.startDate': _.debounce(function() {
      this.filters.page = 1; // Reset to first page
      this.getMeetings();
    }, 300),

    'filters.endDate': _.debounce(function() {
      this.filters.page = 1; // Reset to first page
      this.getMeetings();
    }, 300),

    'filters.search': _.debounce(function() {
      this.filters.page = 1; // Reset to first page
      this.getMeetings();
    }, 500),

    'meetingForm.startTime': function() {
      this.updateEndTime();
    },

    'meetingForm.duration': function() {
      this.updateEndTime();
    }
  }
};
</script>

<style scoped>
/* Add these styles to your <style> section */

/* For preserving whitespace in meeting minutes */
.text-pre-wrap {
  white-space: pre-wrap;
  word-break: break-word;
}

/* Style for the meeting minutes card */
.meeting-minutes-card {
  border-left: 4px solid #17a2b8 !important;
}

/* Style for action items table */
.action-item-row {
  transition: background-color 0.2s ease;
}

.action-item-row:hover {
  background-color: rgba(0, 0, 0, 0.03);
}

/* Add icons for action item status */
.action-item-completed::before {
  content: "✓";
  margin-right: 5px;
  color: #28a745;
}

.action-item-pending::before {
  content: "⟳";
  margin-right: 5px;
  color: #6c757d;
}

.action-item-overdue::before {
  content: "!";
  margin-right: 5px;
  color: #dc3545;
}

.meeting-date-badge {
  min-width: 60px;
}

.date-month {
  text-transform: uppercase;
  font-size: 0.8rem;
  color: #6c757d;
}

.border-left-primary {
  border-left: 4px solid #4e73df !important;
}

.border-left-success {
  border-left: 4px solid #1cc88a !important;
}

.border-left-info {
  border-left: 4px solid #36b9cc !important;
}

.border-left-warning {
  border-left: 4px solid #f6c23e !important;
}

.bg-purple {
  background-color: #6f42c1 !important;
  color: #fff;
}

.bg-indigo {
  background-color: #6610f2 !important;
  color: #fff;
}

.bg-teal {
  background-color: #20c997 !important;
  color: #fff;
}

.bg-pink {
  background-color: #e83e8c !important;
  color: #fff;
}

.bg-orange {
  background-color: #fd7e14 !important;
  color: #fff;
}

/* Add some responsive spacing */
@media (max-width: 768px) {
  .card-body {
    padding: 1rem;
  }

  .upcoming-meeting-item {
    flex-direction: column;
  }

  .meeting-date-badge {
    margin-bottom: 0.75rem;
  }
}
</style>

<style src="@/assets/css/hr-page-shared.css"></style>